/**
 * BookingStatusPanel — the CRM booking-detail status card. Shows the current
 * auto-detected lifecycle status + note + last-updated/source, a full status
 * history modal, and a PIN-gated manual override (with reason, logged).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { History, ShieldCheck, Clock } from 'lucide-react';
import { BookingStatusBadge } from './BookingStatusBadge';
import { STATUS_META, STATUS_ORDER, statusMeta } from '../lib/bookingStatus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingStatusPanel = ({ booking, onChanged }) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingH, setLoadingH] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [ovStatus, setOvStatus] = useState('');
  const [ovReason, setOvReason] = useState('');
  const [ovPin, setOvPin] = useState('');
  const [busy, setBusy] = useState(false);

  const bid = booking.booking_id;
  const status = booking.lifecycle_status || 'pending_payment';
  // Payment amounts live only in the Payments tab — strip any dollar figure that
  // the auto-generated status note may embed (e.g. "…with $166.94 still outstanding").
  const note = (booking.status_note || '').replace(/\$\s?\d[\d,]*(?:\.\d+)?/g, 'a balance');
  const isManual = !!booking.status_override;

  const openHistory = async () => {
    setHistoryOpen(true);
    setLoadingH(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/bookings/${bid}/status-history`, { withCredentials: true });
      setHistory(r.data?.history || []);
    } catch {
      toast.error('Could not load status history');
    } finally {
      setLoadingH(false);
    }
  };

  const submitOverride = async () => {
    if (!ovStatus) { toast.error('Pick a status'); return; }
    if (!ovReason.trim()) { toast.error('A reason is required'); return; }
    if (ovPin.length < 4) { toast.error('Enter your 4-digit staff PIN'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/bookings/${bid}/status-override`,
        { status: ovStatus, reason: ovReason.trim(), pin: ovPin }, { withCredentials: true });
      toast.success('Status updated');
      setOverrideOpen(false); setOvStatus(''); setOvReason(''); setOvPin('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed');
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/bookings/${bid}/status-clear-override`, {}, { withCredentials: true });
      toast.success('Manual override cleared — auto-detection resumed');
      onChanged && onChanged();
    } catch {
      toast.error('Could not clear override');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-[#E8E5DF] p-5" data-testid="bk-status-panel">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="font-semibold text-[#1E1E24] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Booking Status</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={openHistory} data-testid="bk-status-history-btn">
            <History className="w-3 h-3 mr-1" /> History
          </Button>
          <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => { setOvStatus(status); setOverrideOpen(true); }} data-testid="bk-status-override-btn">
            Change
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <BookingStatusBadge status={status} size="lg" testid="bk-status-current" />
        {isManual && <span className="text-[10px] uppercase font-bold text-[#FF7A2E] bg-[#FFF1E8] px-2 py-0.5 rounded-full">manual</span>}
      </div>
      {note && <p className="text-sm text-[#6B6B7A] mt-2" data-testid="bk-status-note">{note}</p>}
      {booking.status_updated_at && (
        <p className="text-[11px] text-[#B7B3AC] mt-1 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Updated {new Date(booking.status_updated_at).toLocaleString()}
          {booking.status_source ? ` · ${booking.status_source}` : ''}
        </p>
      )}
      {isManual && (
        <button onClick={clearOverride} disabled={busy} className="text-xs text-[#FF3B6B] hover:underline mt-2" data-testid="bk-status-clear-override">
          Clear manual override → resume auto-detection
        </button>
      )}

      {/* History modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="bk-status-history-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Status History</DialogTitle>
            <DialogDescription className="text-xs text-[#6B6B7A]">Every automatic and manual status change, newest first.</DialogDescription>
          </DialogHeader>
          {loadingH ? (
            <p className="text-sm text-[#6B6B7A] py-6 text-center">Loading…</p>
          ) : (
            <ol className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {history.map((h, i) => (
                <li key={h.log_id || i} className="flex gap-3" data-testid={`status-log-${i}`}>
                  <span className="w-2 h-2 mt-1.5 rounded-full flex-shrink-0" style={{ background: statusMeta(h.to_status).dot }} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <BookingStatusBadge status={h.to_status} />
                      {h.from_status && <span className="text-[10px] text-[#B7B3AC]">from {statusMeta(h.from_status).label}</span>}
                    </div>
                    {h.note && <p className="text-xs text-[#6B6B7A] mt-0.5">{h.note}</p>}
                    <p className="text-[10px] text-[#B7B3AC] mt-0.5">
                      {h.at ? new Date(h.at).toLocaleString() : ''} · {h.source}{h.actor_name ? ` · ${h.actor_name}` : ''}
                    </p>
                  </div>
                </li>
              ))}
              {history.length === 0 && <p className="text-sm text-[#6B6B7A] py-6 text-center">No history yet.</p>}
            </ol>
          )}
        </DialogContent>
      </Dialog>

      {/* Override dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="bk-status-override-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Change Booking Status</DialogTitle>
            <DialogDescription className="text-xs text-[#6B6B7A]">
              Manually set the status. This overrides auto-detection until cleared. Requires your staff PIN and a reason (logged in history).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[#6B6B7A]">New status</label>
              <select value={ovStatus} onChange={(e) => setOvStatus(e.target.value)} className="w-full h-10 rounded-md border border-[#E8E5DF] px-2 text-sm bg-white mt-1" data-testid="bk-override-status-select">
                {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B6B7A]">Reason (required)</label>
              <Textarea value={ovReason} onChange={(e) => setOvReason(e.target.value)} rows={2} placeholder="e.g. Customer paid cash in the studio" className="mt-1" data-testid="bk-override-reason" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#6B6B7A]">Staff PIN</label>
              <Input type="password" inputMode="numeric" value={ovPin} onChange={(e) => setOvPin(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="••••" className="mt-1 font-mono tracking-widest" data-testid="bk-override-pin" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOverrideOpen(false)} disabled={busy} data-testid="bk-override-cancel">Cancel</Button>
            <Button className="bg-[#FF3B6B] hover:bg-[#e8305c] text-white" onClick={submitOverride} disabled={busy} data-testid="bk-override-submit">
              {busy ? 'Saving…' : 'Update status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default BookingStatusPanel;
